import { Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import useUserCommunities from '@/hooks/useUserCommunities';
import ShareToCommunityModal from '@/components/common/ShareToCommunityModal';

/**
 * Self-contained "Share to Community" button.
 *
 * Renders nothing when the user is not authenticated or has no community
 * memberships. When clicked, opens the ShareToCommunityModal with the
 * provided shareContent pre-filled.
 *
 * @param {object} props
 * @param {'button'|'icon'} props.variant - "button" renders full text,
 *   "icon" renders a compact share icon (for inline use in chat messages).
 * @param {object} props.shareContent - Content to pre-fill in the post form.
 * @param {string} [props.className] - Additional CSS classes.
 */
const ShareToCommunityButton = ({
    variant = 'button',
    shareContent,
    className = '',
}) => {
    const { user } = useAuth();
    const { hasCommunities, loading } = useUserCommunities();
    const [showModal, setShowModal] = useState(false);

    // Don't render if user isn't logged in, has no communities, or still loading
    if (!user || loading || !hasCommunities) {
        return null;
    }

    if (variant === 'icon') {
        return (
            <>
                <button
                    type="button"
                    className={`btn btn-sm btn-link text-muted p-1 ${className}`}
                    onClick={() => setShowModal(true)}
                    aria-label="Share to community"
                    title="Share to community"
                >
                    <i className="bi bi-share" aria-hidden="true" />
                </button>
                <ShareToCommunityModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    shareContent={shareContent}
                />
            </>
        );
    }

    return (
        <>
            <Button
                variant="outline-primary"
                className={`rounded-pill ${className}`}
                onClick={() => setShowModal(true)}
            >
                <i className="bi bi-share me-2" aria-hidden="true" />
                Share to Community
            </Button>
            <ShareToCommunityModal
                show={showModal}
                onHide={() => setShowModal(false)}
                shareContent={shareContent}
            />
        </>
    );
};

ShareToCommunityButton.propTypes = {
    variant: PropTypes.oneOf(['button', 'icon']),
    shareContent: PropTypes.shape({
        postType: PropTypes.oneOf(['discussion', 'resource']),
        title: PropTypes.string,
        content: PropTypes.string,
        resourceUrl: PropTypes.string,
        resourceType: PropTypes.string,
    }).isRequired,
    className: PropTypes.string,
};

export default ShareToCommunityButton;
